import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { listPromptTemplates } from '../../api/promptTemplatesAdmin';
import { getApiErrorMessage } from '../../utils/errors';
import { PER_PAGE } from './constants';
import CategoryFilter from './components/CategoryFilter';
import PromptTemplatesMobileList from './components/PromptTemplatesMobileList';
import PromptTemplatesTable from './components/PromptTemplatesTable';
import SlugFilter from './components/SlugFilter';

const SLUG_FILTER_DEBOUNCE_MS = 300;

function extractCategories(templates) {
  return [...new Set(templates.map((item) => item.category).filter(Boolean))].sort();
}

function PromptTemplatesAdmin() {
  const location = useLocation();
  const successMessage = location.state?.editSuccess
    ? `Prompt "${location.state.slug}" atualizado com sucesso.`
    : null;

  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total: 0 });
  const [categoryFilter, setCategoryFilter] = useState('');
  const [slugFilter, setSlugFilter] = useState('');
  const [debouncedSlugFilter, setDebouncedSlugFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSlugFilter(slugFilter.trim());
    }, SLUG_FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [slugFilter]);

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await listPromptTemplates({ page: 1, per_page: 100 });
      setCategories(extractCategories(response.data || []));
    } catch {
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  const loadTemplates = useCallback(async (page = 1, category = categoryFilter, slug = debouncedSlugFilter) => {
    setLoading(true);
    setError('');

    try {
      const response = await listPromptTemplates({
        page,
        per_page: PER_PAGE,
        ...(category ? { category } : {}),
        ...(slug ? { slug } : {}),
      });
      setTemplates(response.data || []);
      setMeta(response.meta || { page, per_page: PER_PAGE, total: 0 });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os prompts.'));
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, debouncedSlugFilter]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    loadTemplates(1, categoryFilter, debouncedSlugFilter);
  }, [categoryFilter, debouncedSlugFilter, loadTemplates]);

  const handleCategoryChange = (category) => {
    setCategoryFilter(category);
  };

  const handleSlugChange = (slug) => {
    setSlugFilter(slug);
  };

  const handlePageChange = (page) => {
    loadTemplates(page, categoryFilter, debouncedSlugFilter);
  };

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const hasPagination = meta.total > meta.per_page;
  const hasActiveFilters = Boolean(categoryFilter || debouncedSlugFilter);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-content-primary">Prompt templates</h1>
        <p className="text-content-muted text-sm mt-1">
          Curadoria global de prompts do sistema.
        </p>
      </div>

      {successMessage && (
        <p className="text-sm text-forge-700 bg-forge-50 border border-forge-200 rounded-xl px-4 py-3">
          {successMessage}
        </p>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="max-w-xs w-full">
          <CategoryFilter
            value={categoryFilter}
            categories={categories}
            onChange={handleCategoryChange}
            disabled={loadingCategories}
          />
        </div>
        <div className="flex-1 max-w-md w-full">
          <SlugFilter value={slugFilter} onChange={handleSlugChange} disabled={loading} />
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={() => loadTemplates(meta.page, categoryFilter, debouncedSlugFilter)}
            className="btn-secondary text-sm px-3 py-2"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando prompts...</p>
        </div>
      ) : templates.length === 0 && !error ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center">
          <p className="text-content-primary font-medium">Nenhum prompt encontrado</p>
          <p className="text-content-muted text-sm mt-2">
            {hasActiveFilters
              ? 'Nenhum prompt com esses filtros. Tente outros valores.'
              : 'Ainda não há prompts cadastrados no sistema.'}
          </p>
        </div>
      ) : (
        <>
          <PromptTemplatesTable templates={templates} />
          <PromptTemplatesMobileList templates={templates} />

          {hasPagination && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-content-subtle">
                {meta.total} prompt{meta.total !== 1 ? 's' : ''} · página {meta.page} de{' '}
                {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={meta.page <= 1 || loading}
                  onClick={() => handlePageChange(meta.page - 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={meta.page >= totalPages || loading}
                  onClick={() => handlePageChange(meta.page + 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PromptTemplatesAdmin;
